// Wave 13 / Track H — SMPLify-style silhouette refinement (default OFF).
//
// Finite-difference gradient descent on a pixel-wise silhouette-XOR loss.
// Renders a cylinder-approximated body (the same primitives PressureMesh
// uses) into a low-res buffer at the current root_translation, XORs
// against the segmentation mask, and steps root_translation toward the
// loss-reducing direction. The output is a `rootDeltaM` correction to
// the SMPL fit's root.
//
// PRODUCTION-SAFE BY DEFAULT:
//   - Feature-flagged: REACT_APP_SMPLIFY_REFINE must be "true" or "1".
//   - Perf-gated: any call that exceeds 50 ms permanently closes the
//     gate for the rest of the session (every subsequent call returns
//     immediately at zero cost).
//
// PARAMS REFINED: root_translation.{x, y, z} only. Joint pose
// refinement is the obvious next layer — currently scoped out because
// it requires SMPL forward-kinematics + LBS skinning in plain JS. Three
// translation params still capture a substantial fraction of the
// silhouette mismatch on typical riders (the SMPL pose is already a
// solid fit; what drifts is bulk position).
//
// LOSS: |projected_silhouette XOR segmentation_mask| / max(union, 1)
// — a fraction in [0, 1] where 0 is a perfect match.

import * as THREE from 'three';
import { bodyPartFramesFromLandmarks, BodyPartFrame } from './cylinderBody';

export interface SmplRefineInput {
  /** Body joint positions in world coordinates (metres). MediaPipe world
   *  landmarks, Y-up after the pipeline's flip. */
  jointsWorld: Array<{ x: number; y: number; z: number }>;
  /** Segmentation mask: 0 = bg, 1=head, 2=torso, 3=arms, 4=legs. */
  labelMap: Uint8Array;
  maskW: number;
  maskH: number;
  /** Calibration scale (pixels per metre) at the rider's depth. */
  pxPerM: number;
}

export interface SmplRefineResult {
  /** Translation correction in metres. Apply additively to the SMPL
   *  fit's root position. */
  rootDeltaM: { x: number; y: number; z: number };
  /** Whether the refinement actually ran (vs short-circuited by flag/gate). */
  ran: boolean;
  /** Silhouette-XOR loss BEFORE the correction (debug). */
  lossBefore?: number;
  /** Loss AFTER applying the correction (debug). */
  lossAfter?: number;
}

const ZERO_RESULT: SmplRefineResult = {
  rootDeltaM: { x: 0, y: 0, z: 0 },
  ran: false,
};

const PERF_BUDGET_MS = 50;
// Resolution of the offscreen silhouette buffer. Lower = faster, less
// precise. 96 chosen so a typical 480×360 segmentation mask downsamples
// to a manageable grid without losing per-region structure.
const RASTER_RES = 96;
// Finite-difference epsilon (metres). Big enough that a perturbation
// moves at least 1 pixel at typical px_per_m, small enough that the
// gradient is locally linear.
const EPS_M = 0.015;
// SGD step size. Damped so a single bad gradient can't yank the SMPL
// off by more than a few cm.
const LEARNING_RATE = 0.4;
// Max correction magnitude per call (metres). Clamps the worst-case
// when the gradient is spuriously large (e.g., segmentation glitch).
const MAX_STEP_M = 0.05;

// Module-level state: cached env-flag value + perf-gate verdict.
let _enabledCache: boolean | null = null;
let _perfGateOpen = true;

// Scratch buffers reused across calls so we don't pay GC churn.
const _silBuf = new Uint8Array(RASTER_RES * RASTER_RES);
const _maskDown = new Uint8Array(RASTER_RES * RASTER_RES);
let _maskDownStamp = -1; // hash of last labelMap so we only downsample once per frame

function flagEnabled(): boolean {
  if (_enabledCache != null) return _enabledCache;
  const v = (process.env.REACT_APP_SMPLIFY_REFINE ?? '').toString().toLowerCase();
  _enabledCache = v === 'true' || v === '1';
  return _enabledCache;
}

/** Downsample the rider segmentation mask to a binary RASTER_RES × RES
 *  buffer. Any non-zero label becomes 1. Sampling is nearest-neighbor —
 *  good enough for the XOR sum. */
function downsampleMask(labelMap: Uint8Array, maskW: number, maskH: number, out: Uint8Array): void {
  for (let y = 0; y < RASTER_RES; y++) {
    const srcY = Math.floor((y * maskH) / RASTER_RES);
    const srcRow = srcY * maskW;
    const outRow = y * RASTER_RES;
    for (let x = 0; x < RASTER_RES; x++) {
      const srcX = Math.floor((x * maskW) / RASTER_RES);
      out[outRow + x] = labelMap[srcRow + srcX] !== 0 ? 1 : 0;
    }
  }
}

/** Rasterize the cylinder body approximation into the silhouette buffer
 *  given an optional translation offset (in world metres). Uses the same
 *  disc-stamp scheme as regionOcclusion.ts; here we keep just a binary
 *  occupancy (no z-test needed — we just want the silhouette). */
function renderSilhouette(
  parts: BodyPartFrame[],
  dx: number, dy: number, dz: number,
  pxPerM: number,
  maskW: number, maskH: number,
  out: Uint8Array,
): void {
  out.fill(0);
  const sx = RASTER_RES / maskW;
  const sy = RASTER_RES / maskH;
  // Image projection: world (X right, Y up, Z toward camera) → image px.
  // imageCx/cy are the principal point at the source resolution.
  const imageCxPx = maskW / 2;
  const imageCyPx = maskH / 2;
  const project = (wx: number, wy: number, wz: number): { x: number; y: number } => {
    void wz; // weak-perspective: ignore depth at this scale
    const px = imageCxPx + wx * pxPerM;
    const py = imageCyPx - wy * pxPerM;
    return { x: px * sx, y: py * sy };
  };
  const rPxScale = pxPerM * sx; // approximate horizontal px/m at raster scale
  const stamp = (cx: number, cy: number, rPx: number) => {
    const r2 = rPx * rPx;
    const x0 = Math.max(0, Math.floor(cx - rPx));
    const x1 = Math.min(RASTER_RES - 1, Math.ceil(cx + rPx));
    const y0 = Math.max(0, Math.floor(cy - rPx));
    const y1 = Math.min(RASTER_RES - 1, Math.ceil(cy + rPx));
    for (let y = y0; y <= y1; y++) {
      const dy2 = (y - cy) * (y - cy);
      for (let x = x0; x <= x1; x++) {
        const ddx = x - cx;
        if (ddx * ddx + dy2 <= r2) out[y * RASTER_RES + x] = 1;
      }
    }
  };
  for (const part of parts) {
    if (part.visibility < 0.3) continue;
    const rPx = Math.max(1, part.radius * rPxScale);
    if (part.kind === 'sphere') {
      const p = project(part.center.x + dx, part.center.y + dy, part.center.z + dz);
      stamp(p.x, p.y, rPx);
    } else {
      const halfLen = part.length / 2;
      const stepWorld = Math.max(part.radius * 0.5, 0.01);
      const n = Math.max(2, Math.ceil(part.length / stepWorld));
      for (let i = 0; i <= n; i++) {
        const t = -halfLen + (i / n) * part.length;
        const wx = part.center.x + part.axis.x * t + dx;
        const wy = part.center.y + part.axis.y * t + dy;
        const wz = part.center.z + part.axis.z * t + dz;
        const p = project(wx, wy, wz);
        stamp(p.x, p.y, rPx);
      }
    }
  }
}

/** Pixel-wise XOR-loss between two binary buffers, normalized by the
 *  union (Jaccard-like distance). Returns a fraction in [0, 1]. */
function silhouetteLoss(a: Uint8Array, b: Uint8Array): number {
  let xorCount = 0;
  let unionCount = 0;
  for (let i = 0; i < a.length; i++) {
    const av = a[i], bv = b[i];
    if (av || bv) unionCount++;
    if (av !== bv) xorCount++;
  }
  if (unionCount === 0) return 1;
  return xorCount / unionCount;
}

/** Called by the pipeline tick when a fresh SMPL fit lands. Returns a
 *  zeroed result when the flag is off or the perf gate is closed. Both
 *  short-circuits are O(1) — safe to call every frame. */
export function refineSmplBySilhouette(input: SmplRefineInput): SmplRefineResult {
  if (!flagEnabled() || !_perfGateOpen) return ZERO_RESULT;
  const start = performance.now();
  const { jointsWorld, labelMap, maskW, maskH, pxPerM } = input;
  if (!jointsWorld || jointsWorld.length < 33 || pxPerM <= 0) {
    _maybeCloseGate(performance.now() - start);
    return ZERO_RESULT;
  }

  // Build the cylinder body approximation from the supplied joints. This
  // is the same construction PressureMesh uses, so loss minimization on
  // its silhouette directly corresponds to the visualization the rider
  // sees.
  const parts = bodyPartFramesFromLandmarks(
    jointsWorld.map((j) => ({
      x: j.x, y: -j.y, z: j.z, visibility: 1.0,
    })) as any,
  );
  if (!parts.length) {
    _maybeCloseGate(performance.now() - start);
    return ZERO_RESULT;
  }

  // Downsample the segmentation mask once. Mask changes every frame so
  // we always redo this; the stamp-skip is just a placeholder for future
  // micro-optimization.
  downsampleMask(labelMap, maskW, maskH, _maskDown);
  _maskDownStamp = labelMap.length;

  // Baseline render at (0, 0, 0).
  renderSilhouette(parts, 0, 0, 0, pxPerM, maskW, maskH, _silBuf);
  const lossBase = silhouetteLoss(_silBuf, _maskDown);

  // Finite-difference gradient over the 3 translation params.
  const grad: [number, number, number] = [0, 0, 0];
  const axes: Array<[number, number, number]> = [
    [EPS_M, 0, 0],
    [0, EPS_M, 0],
    [0, 0, EPS_M],
  ];
  for (let k = 0; k < 3; k++) {
    const [ax, ay, az] = axes[k];
    renderSilhouette(parts, ax, ay, az, pxPerM, maskW, maskH, _silBuf);
    const lossPlus = silhouetteLoss(_silBuf, _maskDown);
    grad[k] = (lossPlus - lossBase) / EPS_M;
  }

  // SGD step. Clamp to MAX_STEP_M.
  let dxM = -LEARNING_RATE * grad[0];
  let dyM = -LEARNING_RATE * grad[1];
  let dzM = -LEARNING_RATE * grad[2];
  const mag = Math.hypot(dxM, dyM, dzM);
  if (mag > MAX_STEP_M) {
    const scale = MAX_STEP_M / mag;
    dxM *= scale; dyM *= scale; dzM *= scale;
  }

  // Verify the step actually reduces loss before committing it.
  renderSilhouette(parts, dxM, dyM, dzM, pxPerM, maskW, maskH, _silBuf);
  const lossAfter = silhouetteLoss(_silBuf, _maskDown);
  const elapsed = performance.now() - start;
  _maybeCloseGate(elapsed);
  if (lossAfter >= lossBase) {
    // Step would make things worse (gradient was bad / outside locally
    // linear region) — return zero, try again next frame with fresh data.
    return { ...ZERO_RESULT, ran: true, lossBefore: lossBase, lossAfter: lossBase };
  }
  return {
    rootDeltaM: { x: dxM, y: dyM, z: dzM },
    ran: true,
    lossBefore: lossBase,
    lossAfter,
  };
}

function _maybeCloseGate(ms: number): void {
  if (ms > PERF_BUDGET_MS) {
    _perfGateOpen = false;
    // eslint-disable-next-line no-console
    console.warn(
      `[smplify-refine] perf gate closed: first run took ${ms.toFixed(1)}ms > ${PERF_BUDGET_MS}ms budget. Refinement disabled for this device.`,
    );
  }
}

/** Test seam: reset the perf gate + flag cache. Used in dev to flip the
 *  flag at runtime without a page reload. */
export function _resetSmplifyState(): void {
  _enabledCache = null;
  _perfGateOpen = true;
}
